import React, { useState, useEffect, useRef } from "react";
import "./ConversationCard.css";
import ConversationCompletedService from "../../../services/ConversationCompletedService";

import StarRating from "../../StarRating/StarRating";
import Select from "react-select";
import { getCategoryByItem } from "../../../data/Categories";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { FaThumbsUp, FaThumbsDown, FaAlignLeft } from "react-icons/fa";
import getCriteriaData from "/src/features/supervision/conversations/data/getCriteriaData";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import { useMode } from "./ModeContext/ModeContext";
import canMakeReview from "/src/features/supervision/conversations/utils/canMakeReview";
const ConversationCard = ({ data, audioRef, setConversationData, onNavigateToTimestamp }) => {
  const canEdit = canMakeReview();
  const { mode } = useMode();
  const headerRef = useRef(null);
  const theadRef = useRef(null);
  const [isTheadStuck, setIsTheadStuck] = useState(false);

  const toast = useToastDispatchContext();
  const [conversation, setConversation] = useState(data.data)
  const getRightData = (_conversation) => {
    return mode == "translated" ? (_conversation.translate_content?.right || []) : (_conversation.content.right || [])
  }
  const [rightData, setRightData] = useState(getRightData(data.data));

  const criteriasData = data.criterias || [];
  const [criterias, setCriterias] = useState([]);

  const { getCriterias, updateCriteriaConversation, updateDetectionFeedback } = ConversationCompletedService;
  const [openRows, setOpenRows] = useState({}); // {0: true, 1: false, ...}

  const applicabilityChoices = [
    { value: true, label: "Applicable" },
    { value: false, label: "Not applicable" }
  ];
  const [items, setItems] = useState([]);
  const requiredResults = items.filter(item => item.result === "empty").length;


  const handleApplicabilityChange = async (index, option, criteriaId) => {
    const newItems = [...items];

    const [response, error] = await updateCriteriaConversation(conversation.call_id, criteriaId, {
      applicability: option.value
    });
    if (error) {
      console.error("Failed to update criteria conversation:", error);
      return;
    }
    // Also patch the shared conversation.criterias, not just this component's
    // local items - otherwise a sibling update (e.g. adding a criteria from
    // the transcription modal) replaces conversation.criterias with a stale
    // copy and this edit gets silently reverted without a page refresh.
    setConversationData(prev => {
      const hasRow = prev.criterias?.some(c => c.id === response.id);
      const criterias = hasRow
        ? prev.criterias.map(c => c.id === response.id ? { ...c, ...response } : c)
        : [...(prev.criterias || []), response];
      return {
        ...prev,
        criterias,
        data: {
          ...prev.data,
          score: response.conversation_score
        }
      };
    });
    toast.success("Applicability Saved", "The applicability has been saved successfully.");
    newItems[index].applicability = option.value;
    newItems[index].score = response.weight;

    setItems(newItems);
  };

  const handleLikeChange = async (index, rIndex, value, id, segmentIndex) => {
    const newItems = [...items];
    const newValue = newItems[index].rows[rIndex].like === value ? null : value;

    const [response, error] = await updateDetectionFeedback(id, segmentIndex, {
      like: newValue
    });
    if (error) {
      console.error("Failed to update detection feedback:", error);
      return;
    }
    toast.success("Feedback Saved", "The feedback has been saved successfully.");
    newItems[index].rows[rIndex].like = newValue;
    setItems(newItems);
  };

  const handleResultClick = async (index, type, criteriaId) => {
    const newItems = [...items];
    const [response, error] = await updateCriteriaConversation(conversation.call_id, criteriaId, {
      agent_result: type
    });
    if (error) {
      console.error("Failed to update criteria conversation:", error);
      return;
    }
    // Same rationale as handleApplicabilityChange: keep the shared
    // conversation.criterias in sync so it doesn't stomp this edit later.
    setConversationData(prev => {
      const hasRow = prev.criterias?.some(c => c.id === response.id);
      const criterias = hasRow
        ? prev.criterias.map(c => c.id === response.id ? { ...c, ...response } : c)
        : [...(prev.criterias || []), response];
      return {
        ...prev,
        criterias,
        data: {
          ...prev.data,
          score: response.conversation_score
        }
      };
    });
    toast.success("Result Saved", "The result has been saved successfully.");
    newItems[index].result = response.final_result;
    newItems[index].agentResult = response.agent_result;
    newItems[index].score = response.weight;
    setItems(newItems);
  };

  const toggleRow = (index) => {
    setOpenRows(prev => ({ ...prev, [index]: !prev[index] }));
  };
  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }

  const handleCommentChange = (index, value) => {
    setItems(prev => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        comment: value
      };
      return copy;
    });
  };

  const handleCommentBlur = async (index, criteriaId) => {
    const item = items[index];
    const [response, error] = await updateCriteriaConversation(conversation.call_id, criteriaId, {
      comment: item.comment
    });

    if (error) {
      console.error(error);
      return;
    }
    // Same rationale as handleApplicabilityChange/handleResultClick.
    setConversationData(prev => {
      const hasRow = prev.criterias?.some(c => c.id === response.id);
      const criterias = hasRow
        ? prev.criterias.map(c => c.id === response.id ? { ...c, ...response } : c)
        : [...(prev.criterias || []), response];
      return { ...prev, criterias };
    });
    toast.success("Comment Saved", "The comment has been saved successfully.");
  };

  const getColorByBlowUp = (item) => {
    if (item.weight == 100) {
      return "grave-blow-up-row"
    }
    else if (item.weight == 50) {
      return "medium-blow-up-row"
    }
    else if (item.weight == 30) {
      return "low-blow-up-row"
    }
    return "";
  }

  useEffect(() => {
    if (criterias.length === 0 || rightData == null || rightData.length == 0) return;

    const allCriteria = criterias.map(criteria => {
      const criteriaData = getCriteriaData(criteriasData, criteria.id);

      const rows = rightData.filter(
        row => row.criterias?.includes(criteria.id)
      );
      return {
        ...criteria,
        criteria_conversation_id: criteriaData?.id,
        category: getCategoryByItem(criteria),

        detections: criteriaData?.detection || 0,
        result: criteriaData?.final_result || "empty",
        agentResult: criteriaData?.agent_result || null,
        applicability: criteriaData?.applicability ?? 1,
        comment: criteriaData?.comment || "",
        rows: rows.map(r => ({
          text: r.text,
          start: r.start,
          end: r.end,
          segment_index: r.segment_index,
          like: criteriaData?.detection_likes?.[r.segment_index] ?? null
        }))
      };
    });

    setItems(allCriteria);
  }, [criterias, criteriasData, rightData]);

  useEffect(() => {
    var fetchCriterias = async () => {
      const [response, error] = await getCriterias({
        unit_id: conversation.unit_id
      });
      if (error || !response) {
        console.error("Failed to fetch criterias:", error);
        return;
      }
      setCriterias(response);
    }
    fetchCriterias();
  }, []);

  useEffect(() => {
    setConversation(data.data);
    setRightData(getRightData(data.data))
  }, [data])

  useEffect(() => {
    setRightData(getRightData(conversation))
  }, [mode])

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const updateHeight = () => {
      document.documentElement.style.setProperty('--conversation-card-header-height', `${el.offsetHeight}px`);
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkStuck = () => {
      const el = theadRef.current;
      if (!el) return;
      const stickyTop = parseFloat(getComputedStyle(el).top) || 0;
      setIsTheadStuck(el.getBoundingClientRect().top <= stickyTop + 1);
    };

    checkStuck();
    window.addEventListener('scroll', checkStuck, { passive: true, capture: true });
    window.addEventListener('resize', checkStuck);
    return () => {
      window.removeEventListener('scroll', checkStuck, { capture: true });
      window.removeEventListener('resize', checkStuck);
    };
  }, []);

  return (
    <div className="conversation-card-wrapper">
      <div className="conversation-card-header" ref={headerRef}>
        Compliance score
        <StarRating score={conversation.score} />
        {
          requiredResults >= 1 &&
          <Tippy
            className="criteria-tooltip"
            content={
              <div className="criteria-warning-tooltip">
                Action required - Result can't be auto-assessed
              </div>
            }
            placement="top"
            delay={[200, 0]}
          >
            <div className="conversation-result-require">
              <i className="fas fa-exclamation-triangle icon_empty"></i>
              {requiredResults} result{requiredResults > 1 ? "s" : ""} required
            </div>
          </Tippy>

        }

      </div>

      <table className="conversation-card">
        <thead ref={theadRef} className={isTheadStuck ? "is-stuck" : ""}>
          <tr>
            <th style={{ width: "10%" }}>Applicability</th>
            <th style={{ width: "20%" }}>Criteria</th>
            <th style={{ width: "10%" }}>Category</th>
            <th style={{ width: "5%" }}>Detections</th>
            <th style={{ width: "5%" }}>Result</th>
            <th style={{ width: "5%" }}>Score</th>
            <th style={{ width: "20%" }}>Comments</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <React.Fragment key={index}>
              <tr className={getColorByBlowUp(item)}>
                <td>
                  <Select
                    value={item.applicability === false ? applicabilityChoices[1] : applicabilityChoices[0]}
                    options={applicabilityChoices}
                    onChange={(option) => handleApplicabilityChange(index, option, item.id)}
                    isSearchable={false}
                    styles={{
                      container: (base) => ({
                        ...base
                      })
                    }}
                    isDisabled={conversation.is_closed || !canEdit}
                  />
                </td>
                <td className="criteria-cell" title={item.name + " - " + item.description}>
                  <div className="criteria-name"><span>{item.name}</span></div>
                  <div className="criteria-description"> <span> {item.description}</span></div>
                </td>

                <td className="category-cell" title={item.name + (item.category ? " - " + item.category.name : "")}>
                  <span>{item.category?.name || "Unknown"}</span>
                </td>

                <td onClick={() => toggleRow(index)} style={{ cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center", height: "50px" }}>
                  {item.detections >= 1 ? (
                    <span className="detection-badge" style={{ backgroundColor: item.color }}>{item.detections}</span>
                  ) : (
                    <span></span>
                  )}
                </td>

                <td className="result-cell">
                  <div className="result-container">
                    <div className="result">
                      <button
                        className={item.result === "pass" ? "checked" : ""}
                        onClick={() => handleResultClick(index, "pass", item.id)}
                        disabled={conversation.is_closed || !canEdit}
                      >
                        ✓
                      </button>
                      <button
                        className={item.result === "fail" ? "cross" : ""}
                        onClick={() => handleResultClick(index, "fail", item.id)}
                        disabled={conversation.is_closed || !canEdit}
                      >
                        ✕
                      </button>
                    </div>
                    {item.agentResult ? (
                      <Tippy
                        className="criteria-tooltip-info"
                        content={
                          <div className="criteria-info-tooltip">
                            Result set manually by a reviewer
                          </div>
                        }
                        placement="top"
                        delay={[200, 0]}
                      >
                        <i className="fas fa-user icon_agent_override"></i>
                      </Tippy>
                    ) : item.result === "empty" && (
                      <Tippy
                        className="criteria-tooltip"
                        content={
                          <div className="criteria-warning-tooltip">
                            Action required - Result can't be auto-assessed
                          </div>
                        }
                        placement="top"
                        delay={[200, 0]}
                      >
                        <i className="fas fa-exclamation-triangle icon_empty"></i>
                      </Tippy>
                    )}
                  </div>
                </td>

                <td className="score">
                  {item.result === "fail" ?
                    (item.applicability == false ?
                      <div className='conversation-result-require' style={{ background: "unset", border: "unset", color: "lightgray" }}>-{item.weight}</div>

                      :
                      // A reviewer failing this manually counts in full even with
                      // 0 detections - matches compute_conversation_score's
                      // agent_result bypass of the detection gate.
                      (item.detections > 0 || item.agentResult === "fail") && item.weight > 0 && <div className='conversation-result-require' style={{ color: "var(--pink)" }}>-{item.weight}</div>

                    ) : ""}
                </td>
                <td className="comments">
                  <input disabled={conversation.is_closed || !canEdit} type="text" value={item.comment || ""} placeholder="Free text..." onChange={(e) => handleCommentChange(index, e.target.value)} onBlur={() => handleCommentBlur(index, item.id)} />
                </td>
              </tr>
              {openRows[index] && item.detections >= 1 && (
                <tr className="detection-detail-row">
                  <td colSpan={7} style={{ background: "#f9fafb", paddingTop: "0px" }}>
                    <table className="sub-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th className="row-column-detail-detection">Transcription</th>
                          <th className="row-column-detail-detection" style={{ width: "80px" }}>Feedback</th>
                          <th className="row-column-detail-detection" style={{ width: "150px" }}>Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.rows?.map((row, rIndex) => {
                          return (
                            <tr key={rIndex}>
                              <td className="row-text-detail-detection">{row.text}</td>
                              <td className="row-text-detail-feedback">
                                <div className="detection-feedback">
                                  <button
                                    key={rIndex + "liked_btn"}
                                    className={row.like === true ? "liked" : ""}
                                    title="Like"
                                    onClick={() => handleLikeChange(index, rIndex, true, item.criteria_conversation_id, row.segment_index)}
                                    disabled={conversation.is_closed || !canEdit}
                                  >
                                    <FaThumbsUp />
                                  </button>
                                  <button
                                    key={rIndex + "disliked_btn"}
                                    className={row.like === false ? "disliked" : ""}
                                    title="Dislike"
                                    onClick={() => handleLikeChange(index, rIndex, false, item.criteria_conversation_id, row.segment_index)}
                                    disabled={conversation.is_closed || !canEdit}
                                  >
                                    <FaThumbsDown />
                                  </button>
                                </div>
                              </td>
                              <td className="row-text-detail-detection detail-timestamp-cell">
                                <div className="detail-timestamp-container">
                                  <button className="goto-transcript-button" title="Open in transcription" onClick={() => onNavigateToTimestamp?.(row.start)}>
                                    <FaAlignLeft />
                                  </button>
                                  <span
                                    className="detail-timestamp"
                                    onClick={() => {
                                      if (audioRef?.current?.audio?.current) {
                                        audioRef.current.audio.current.currentTime = row.start;
                                        audioRef.current.audio.current.play();
                                      }
                                    }}
                                  >{formatTime(row.start)}</span>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <br></br>
      <br></br>
      <br></br>
      <br></br>
    </div>
  );
};

export default ConversationCard;